import React, { useMemo } from 'react';
import { View } from 'react-native';
import Animated from 'react-native-reanimated';
import { decode } from 'html-entities';
import { format, parseISO } from 'date-fns';
import { rem, AppView, CardView, SettingsItem, SCROLL_INDICATOR_INSETS, SCREEN_BOTTOM_PADDING, } from 'design-system';
import Text from 'design-system/Text';
import Section from 'design-system/Section';
import { IconEmail, IconPhone } from 'design-system/icons';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { formatUnsignedInteger } from 'utils/formatting';
import withAnimatedNavigationHeader from 'design-system/hoc/withAnimatedNavigationHeader';
import IconShieldFail from 'design-system/icons/IconShieldFail';
import { useMarginBottom } from 'hooks/useMarginBottom';
import { isWeb } from '../../constants';
const BreachScreen = withAnimatedNavigationHeader(({ route, scrollHandler }) => {
    const { breach } = route.params;
    const styles = useStyles(styleSet);
    const phones = breach?.compromisedAccounts?.filter((acc) => acc?.accountType === 'phone');
    const emails = breach?.compromisedAccounts?.filter((acc) => acc?.accountType === 'email');
    const decodedDescription = useMemo(() => {
        if (!breach?.description)
            return null;
        // Remove HTML tags, the description string contains <a>links</a>
        const replaced = breach.description?.replace(/(<([^>]+)>)/gi, '');
        // Decode HTML entities for example &quot;text&qout; -> "text"
        return decode(replaced);
    }, [breach]);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const contentContainerStyle = useMarginBottom('paddingBottom', SCREEN_BOTTOM_PADDING, paddingHorizontalStyle);
    return (<AppView style={styles.container} withFrame={false}>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <CardView noMarginBottom>
            <View style={styles.breachTitleField}>
              <Text Text-20 style={styles.breachTitle}>
                {breach?.title}
              </Text>

              <View style={styles.circle}>
                <IconShieldFail colorKey="statusNegative"/>
              </View>
            </View>
            {Boolean(decodedDescription) && (<View style={styles.breachDescContainer}>
                <Text TextThin-16 Secondary>
                  {decodedDescription}
                </Text>
              </View>)}
            <View style={styles.breachTile}>
              <Text style={styles.breachTileLeft} TextThin-14 Secondary>
                People affected
              </Text>
              <Text Text-16>{formatUnsignedInteger(breach?.pwnCount)}</Text>
            </View>
            {!!breach?.breachDateAt && (<View style={styles.date}>
                <Text style={styles.breachTileLeft} TextThin-14 Secondary>
                  Date
                </Text>
                <Text Text-16>
                  {format(parseISO(breach.breachDateAt), 'MMMM d, yyyy')}
                </Text>
              </View>)}
          </CardView>

          <View style={styles.accounts}>
            {phones?.length > 0 && (<Section title="Affected phone numbers" style={styles.section}>
                <CardView noMarginTop noMarginBottom>
                  {phones.map((el) => (<SettingsItem key={el.id} iconSize={44} icon={<IconPhone gradientKey="darkPurple"/>} title={el.account}/>))}
                </CardView>
              </Section>)}
            {emails?.length > 0 && (<Section title="Affected emails" style={styles.section}>
                <CardView noMarginTop noMarginBottom>
                  {emails.map((el) => (<SettingsItem key={el.id} iconSize={44} icon={<IconEmail gradientKey="darkPink"/>} title={el.account}/>))}
                </CardView>
              </Section>)}
          </View>
          <View style={styles.poweredByContainer}>
            <Text style={styles.footTop} Text-14>
              Powered by
            </Text>
            <Text Text-14 Secondary style={styles.letterSpacing}>
              haveibeenpwned.com
            </Text>
          </View>
        </Animated.ScrollView>
      </AppView>);
}, ({ colors }) => ({
    title: 'Fraud details',
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    container: {
        flex: 1,
    },
    content: {
        flex: 1,
    },
    breachTitleField: {
        width: '100%',
        alignItems: 'center',
        flexDirection: 'row',
        padding: rem(16),
        justifyContent: 'space-between',
    },
    breachTitle: {
        flexShrink: 1,
    },
    breachDescContainer: {
        width: '100%',
        padding: rem(16),
        borderTopWidth: 1,
        borderColor: colors.borders.divider,
    },
    breachTile: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(16),
        paddingVertical: rem(16),
        borderTopWidth: 1,
        borderColor: colors.borders.divider,
    },
    date: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(16),
        paddingVertical: rem(16),
    },
    breachTileLeft: {
        flex: 1,
        flexGrow: 1,
    },
    breachAcc: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: rem(16),
        marginVertical: rem(8),
    },
    breachAccLeft: {
        flex: 1,
    },
    breachAccRight: {
        width: rem(24),
        height: rem(24),
        marginRight: rem(2),
    },
    accounts: {
        width: '100%',
    },
    poweredByContainer: {
        paddingTop: rem(32),
        paddingBottom: rem(40),
        alignItems: 'center',
    },
    footTop: {
        marginBottom: rem(8),
    },
    section: {
        marginTop: rem(24),
        marginBottom: 0,
    },
    circle: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(44),
        backgroundColor: colors.background.dark,
        alignItems: 'center',
        justifyContent: 'center',
    },
    letterSpacing: {
        letterSpacing: 0.4,
    },
}));
export default BreachScreen;
